import { fetcher } from '@app/hooks/fetcher.tsx';
import { Routes } from '@app/routes';
import { SWRConfig } from 'swr';
import ThemeProvider, { ThemeContext } from './theme/ThemeProvider';
import { RecoilRoot } from 'recoil';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type CustomCache = Map<string, any> & Cache;

function localStorageProvider() {
  let initialEntries: [string, any][] = [];
  try {
    const raw = localStorage.getItem('app-cache') || '[]';
    initialEntries = JSON.parse(raw);
    if (!Array.isArray(initialEntries)) {
      initialEntries = [];
    }
  } catch (e) {
    // 손상된 캐시 방지: 비우기
    try {
      localStorage.removeItem('app-cache');
    } catch {}
    initialEntries = [];
  }

  const map = new Map(initialEntries);

  const persistCacheSafely = () => {
    try {
      const appCache = JSON.stringify(Array.from(map.entries()));
      // 대략적인 5MB 한도 대비 여유 버퍼
      if (appCache.length > 4500000) {
        // 용량 초과 예상 시 캐시를 비움
        localStorage.setItem('app-cache', '[]');
        return;
      }
      localStorage.setItem('app-cache', appCache);
    } catch (err: any) {
      // QuotaExceeded 혹은 직렬화 오류 시 캐시 삭제로 복구
      try {
        localStorage.removeItem('app-cache');
      } catch {}
    }
  };

  // 종료/페이지 숨김 시에만 저장하여 write 빈도 감소
  window.addEventListener('beforeunload', persistCacheSafely);
  window.addEventListener('pagehide', persistCacheSafely);

  return map as CustomCache;
}

const App = () => {
  const { isDarkMode, ts } = useContext(ThemeContext);
  const { i18n } = useTranslation();

  useEffect(() => {
    console.log('App is running in dark mode', isDarkMode);
    console.log('App is running in dark mode', ts);
  }, [isDarkMode, ts]);

  // 앱이 시작될 때 localStorage와 쿠키에서 언어 설정을 가져와 적용
  useEffect(() => {
    // 1. localStorage에서 언어 설정 확인
    const localStorageLng = localStorage.getItem('i18nextLng');
    
    // 2. 쿠키에서 언어 설정 확인
    let cookieLng: string | null = null;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith('i18nextLng=')) {
        cookieLng = cookie.substring('i18nextLng='.length, cookie.length);
        break;
      }
    }
    
    // 3. 사용할 언어 결정 (localStorage > 쿠키 > 기본값)
    const languageToUse = localStorageLng || cookieLng || i18n.language || 'ko';
    
    // 언어가 변경되었는지 확인 후 적용
    if (languageToUse && languageToUse !== i18n.language) {
      console.log(`App: Changing language to ${languageToUse}`);
      i18n.changeLanguage(languageToUse);
    }
    
    // 설정을 localStorage와 쿠키에 모두 저장하여 동기화
    if (languageToUse) {
      localStorage.setItem('i18nextLng', languageToUse);
      
      // 쿠키에도 저장 (1년 유효기간)
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);
      document.cookie = `i18nextLng=${languageToUse}; path=/; expires=${expirationDate.toUTCString()}; SameSite=Strict; secure=${window.location.protocol === 'https:'}`;
    }
  }, [i18n]);

  return (
    <RecoilRoot>
      <ThemeProvider>
        <GoogleOAuthProvider
          clientId={
            process.env.VITE_APP_GOOGLE_CLIEND_ID ||
            '330184879260-5hq3eu99cbpid1jjnmmn1sll7hvb46bm.apps.googleusercontent.com'
          }
        >
          <SWRConfig
            value={{
              fetcher: fetcher,
              provider: localStorageProvider,
            }}
          >
            <Routes />
          </SWRConfig>
        </GoogleOAuthProvider>
      </ThemeProvider>
    </RecoilRoot>
  );
};

export default App;
